import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { HILLS_BANK_CATEGORIES } from '@/lib/hillsBankCategories';
import {
  Plus,
  Trash2,
  Upload,
  FileText,
  MoreVertical,
  DollarSign,
  Calendar,
  Pencil,
  ChevronDown,
  ChevronRight,
  Receipt,
  RefreshCw,
  TrendingUp,
  Zap,
  Truck,
  Trash,
  Droplets,
  Shield,
  Home,
  Wallet,
  Package,
  Clock,
  CheckCircle2,
  Wand2
} from 'lucide-react';

const HOLDING_COST_CATEGORY_LABELS = {
  utilities: { label: 'Utilities', icon: Droplets },
  dumpster: { label: 'Dumpster', icon: Trash },
  delivery_fees: { label: 'Delivery Fees', icon: Truck },
  cleaning: { label: 'Cleaning', icon: Home },
  portable_toilet: { label: 'Portable Toilet', icon: Package },
  security_fencing: { label: 'Security/Fencing', icon: Shield },
  temporary_power: { label: 'Temporary Power', icon: Zap },
  insurance: { label: 'Insurance', icon: Shield },
  property_taxes: { label: 'Property Taxes', icon: Wallet },
  storage: { label: 'Storage', icon: Package },
  loan_interest: { label: 'Loan Interest', icon: TrendingUp },
  other: { label: 'Other', icon: Receipt }
};

export const HoldingCostsCard = ({ projectId, project, isReadOnly = false }) => {
  const [holdingCosts, setHoldingCosts] = useState([]);
  const [loanInterest, setLoanInterest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Add Cost Dialog
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newCost, setNewCost] = useState({
    category: '',
    name: '',
    vendor: '',
    bank_category: '__none__',
    estimated_cost: '',
    actual_cost: '',
    is_recurring: false
  });
  const [addCostReceiptFile, setAddCostReceiptFile] = useState(null);
  const [isExtractingReceipt, setIsExtractingReceipt] = useState(false);
  
  // Edit Cost Dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // Add Entry Dialog (for recurring costs)
  const [isAddEntryDialogOpen, setIsAddEntryDialogOpen] = useState(false);
  const [addingEntryToCost, setAddingEntryToCost] = useState(null);
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    is_paid: false,
    paid_date: '',
    notes: ''
  });
  
  // View Entries Dialog
  const [isViewEntriesDialogOpen, setIsViewEntriesDialogOpen] = useState(false);
  const [viewingEntriesCost, setViewingEntriesCost] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // Retry transient blips (429 / 5xx / network / racy 401 on tab resume)
      // so returning to the tab after a download doesn't flash a scary
      // "Failed to load holding costs" toast when the data is actually fine.
      let res, lastErr;
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          res = await apiService.getHoldingCosts(projectId);
          break;
        } catch (e) {
          lastErr = e;
          const st = e?.response?.status;
          const transient = st === 429 || st === 401 || (st >= 500 && st <= 599) || !e?.response;
          if (transient && attempt < 3) {
            await new Promise(r => setTimeout(r, 350 * Math.pow(2, attempt)));
            continue;
          }
          throw e;
        }
      }
      if (!res) throw lastErr;
      setHoldingCosts(res.data.holding_costs || []);
      setLoanInterest(res.data.loan_interest || null);
    } catch (err) {
      // Background/transient failure — log quietly and keep any prior data
      // rather than alarming the user with a toast on a tab-resume refresh.
      console.error('Failed to load holding costs:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calculate totals
  const totalEstimated = holdingCosts.reduce((sum, c) => sum + (c.estimated_cost || 0), 0);
  const totalActual = holdingCosts.reduce((sum, c) => sum + (c.actual_cost || 0), 0) + (loanInterest?.total_accrued || 0);
  const totalPaid = holdingCosts.reduce((sum, c) => {
    if (c.is_recurring) {
      return sum + (c.entries || []).filter(e => e.is_paid).reduce((s, e) => s + (e.amount || 0), 0);
    }
    return sum + (c.is_paid ? (c.actual_cost || 0) : 0);
  }, 0);

  // Get bank category label
  const getBankCategoryLabel = (code) => {
    if (!code || code === '__none__') return null;
    const cat = HILLS_BANK_CATEGORIES.find(c => c.fullName === code || c.code === code);
    return cat ? cat.fullName : code;
  };

  // Handle add cost
  const handleAddCost = async () => {
    if (!newCost.category || !newCost.name) {
      toast.error('Please fill in category and name');
      return;
    }
    
    try {
      const data = {
        ...newCost,
        bank_category: newCost.bank_category === '__none__' ? null : newCost.bank_category,
        estimated_cost: parseFloat(newCost.estimated_cost) || 0,
        actual_cost: parseFloat(newCost.actual_cost) || 0
      };
      
      const res = await apiService.createHoldingCost(projectId, data);
      setHoldingCosts(prev => [...prev, res.data.holding_cost]);
      setIsAddDialogOpen(false);
      setNewCost({
        category: '',
        name: '',
        vendor: '',
        bank_category: '__none__',
        estimated_cost: '',
        actual_cost: '',
        is_recurring: false
      });
      setAddCostReceiptFile(null);
      toast.success('Holding cost added');
    } catch (err) {
      toast.error('Failed to add holding cost');
    }
  };

  // AI Receipt Extraction for Add Holding Cost dialog
  const handleExtractReceiptForAddCost = async (file) => {
    if (!file) return;
    
    setIsExtractingReceipt(true);
    setAddCostReceiptFile(file);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiService.extractReceiptData(formData);
      
      if (res.data.success && res.data.extracted_data) {
        const data = res.data.extracted_data;
        
        // Try to determine holding cost category from description/vendor
        let category = 'other';
        const text = `${data.description || ''} ${data.vendor_name || ''}`.toLowerCase();
        if (text.includes('dumpster') || text.includes('waste')) category = 'dumpster';
        else if (text.includes('electric') || text.includes('util') || text.includes('power') || text.includes('gas') || text.includes('water')) category = 'utilities';
        else if (text.includes('delivery')) category = 'delivery_fees';
        else if (text.includes('clean')) category = 'cleaning';
        else if (text.includes('toilet') || text.includes('porta')) category = 'portable_toilet';
        else if (text.includes('secur') || text.includes('fence')) category = 'security_fencing';
        else if (text.includes('insur')) category = 'insurance';
        else if (text.includes('tax')) category = 'property_taxes';
        else if (text.includes('storage')) category = 'storage';
        
        // Auto-fill the form with extracted data
        setNewCost(prev => ({
          ...prev,
          category: category,
          name: data.description || data.vendor_name || HOLDING_COST_CATEGORY_LABELS[category]?.label || prev.name,
          vendor: data.vendor_name || prev.vendor,
          estimated_cost: data.total_amount || prev.estimated_cost,
          actual_cost: data.total_amount || prev.actual_cost,
          bank_category: data.suggested_bank_category || prev.bank_category
        }));
        
        toast.success('Receipt data extracted successfully!');
      } else {
        toast.error(res.data.error || 'Could not extract data from receipt');
      }
    } catch (err) {
      console.error('Receipt extraction failed:', err);
      toast.error('Failed to extract receipt data');
    } finally {
      setIsExtractingReceipt(false);
    }
  };

  // Handle update cost
  const handleUpdateCost = async () => {
    if (!editingCost) return;
    
    try {
      const data = {
        ...editFormData,
        bank_category: editFormData.bank_category === '__none__' ? null : editFormData.bank_category,
        estimated_cost: parseFloat(editFormData.estimated_cost) || 0,
        actual_cost: parseFloat(editFormData.actual_cost) || 0
      };
      
      await apiService.updateHoldingCost(projectId, editingCost.id, data);
      setHoldingCosts(prev => prev.map(c => 
        c.id === editingCost.id ? { ...c, ...data } : c
      ));
      setIsEditDialogOpen(false);
      setEditingCost(null);
      toast.success('Holding cost updated');
    } catch (err) {
      toast.error('Failed to update holding cost');
    }
  };

  // Handle delete cost
  const handleDeleteCost = async (costId) => {
    try {
      await apiService.deleteHoldingCost(projectId, costId);
      setHoldingCosts(prev => prev.filter(c => c.id !== costId));
      toast.success('Holding cost deleted');
    } catch (err) {
      toast.error('Failed to delete holding cost');
    }
  };

  // Handle add entry (for recurring costs)
  const handleAddEntry = async () => {
    if (!addingEntryToCost || !newEntry.amount) {
      toast.error('Please enter an amount');
      return;
    }
    
    try {
      const res = await apiService.addHoldingCostEntry(projectId, addingEntryToCost.id, {
        date: newEntry.date,
        amount: parseFloat(newEntry.amount) || 0,
        is_paid: newEntry.is_paid,
        paid_date: newEntry.is_paid ? newEntry.paid_date : null,
        notes: newEntry.notes || null
      });
      
      // Update local state
      setHoldingCosts(prev => prev.map(c => {
        if (c.id === addingEntryToCost.id) {
          return {
            ...c,
            entries: [...(c.entries || []), res.data.entry],
            actual_cost: res.data.total_actual
          };
        }
        return c;
      }));
      
      setIsAddEntryDialogOpen(false);
      setAddingEntryToCost(null);
      setNewEntry({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        is_paid: false,
        paid_date: '',
        notes: ''
      });
      toast.success('Entry added');
    } catch (err) {
      toast.error('Failed to add entry');
    }
  };

  // Handle receipt upload
  const handleReceiptUpload = async (costId, file) => {
    try {
      await apiService.uploadHoldingCostReceipt(projectId, costId, file);
      setHoldingCosts(prev => prev.map(c => 
        c.id === costId ? { ...c, receipt_file_id: 'uploaded' } : c
      ));
      toast.success('Receipt uploaded');
    } catch (err) {
      toast.error('Failed to upload receipt');
    }
  };

  // Open edit dialog
  const openEditDialog = (cost) => {
    setEditingCost(cost);
    setEditFormData({
      category: cost.category || '',
      name: cost.name || '',
      vendor: cost.vendor || '',
      bank_category: cost.bank_category || '__none__',
      estimated_cost: cost.estimated_cost || 0,
      actual_cost: cost.actual_cost || 0,
      is_recurring: cost.is_recurring || false,
      is_paid: cost.is_paid || false,
      paid_date: cost.paid_date || ''
    });
    setIsEditDialogOpen(true);
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const config = HOLDING_COST_CATEGORY_LABELS[category];
    if (config) {
      const IconComponent = config.icon;
      return <IconComponent className="w-4 h-4" />;
    }
    return <Receipt className="w-4 h-4" />;
  };

  // Get category label
  const getCategoryLabel = (category) => {
    return HOLDING_COST_CATEGORY_LABELS[category]?.label || category;
  };

  if (loading) {
    return (
      <Card className="bg-white rounded-xl shadow-sm">
        <CardContent className="p-4 text-center">
          <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-white rounded-xl shadow-sm border border-slate-200" data-testid="holding-costs-card">
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleTrigger asChild>
            <div className="p-4 cursor-pointer hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                  <div className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-slate-800">Holding Costs</h3>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {holdingCosts.length} items
                  </Badge>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Estimated</p>
                    <p className="font-semibold text-slate-800">${totalEstimated.toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Actual</p>
                    <p className="font-semibold text-purple-600">${totalActual.toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Paid</p>
                    <p className="font-semibold text-green-600">${totalPaid.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <CardContent className="pt-0 px-4 pb-4">
              {/* Add Cost Button - Hidden for read-only users */}
              {!isReadOnly && (
                <div className="flex justify-end mb-4">
                  <Button
                    size="sm"
                    onClick={() => setIsAddDialogOpen(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="add-holding-cost-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Holding Cost
                  </Button>
                </div>
              )}
              
              {/* Auto-Synced Loan Interest */}
              {loanInterest && loanInterest.total_accrued > 0 && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-4 border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">Loan Interest</span>
                          <Badge className="bg-blue-100 text-blue-700 text-xs">Auto-Synced</Badge>
                        </div>
                        <p className="text-xs text-slate-500">
                          {loanInterest.current_interest_rate}% APR on ${(loanInterest.principal || 0).toLocaleString()} drawn
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Accrued to Date ({loanInterest.days_accrued || 0} days)</p>
                      <p className="text-xl font-bold text-blue-600">${(loanInterest.total_accrued || 0).toLocaleString()}</p>
                      <p className="text-xs text-slate-400">~${(loanInterest.daily_rate || 0).toFixed(2)}/day</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Holding Costs List */}
              {holdingCosts.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Wallet className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No holding costs added yet</p>
                  <p className="text-sm">Track ongoing expenses like utilities, dumpster, cleaning, etc.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {holdingCosts.map(cost => (
                    <div 
                      key={cost.id} 
                      className="bg-slate-50 rounded-lg p-3 border border-slate-200 hover:border-slate-300 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border border-slate-200">
                            {getCategoryIcon(cost.category)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{cost.name}</span>
                              {cost.is_recurring && (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Recurring
                                </Badge>
                              )}
                              {cost.is_paid && !cost.is_recurring && (
                                <Badge className="bg-green-100 text-green-700 text-xs">Paid</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <span>{getCategoryLabel(cost.category)}</span>
                              {cost.vendor && <span>- {cost.vendor}</span>}
                              {cost.bank_category && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600">
                                  {getBankCategoryLabel(cost.bank_category)}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Est: ${(cost.estimated_cost || 0).toLocaleString()}</p>
                            <p className="font-semibold text-sm">
                              Actual: ${(cost.actual_cost || 0).toLocaleString()}
                            </p>
                            {cost.is_recurring && cost.entries?.length > 0 && (
                              <p className="text-xs text-slate-400">{cost.entries.length} entries</p>
                            )}
                          </div>
                          
                          {/* Actions dropdown - Hidden for read-only users */}
                          {!isReadOnly && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(cost)}>
                                  <Pencil className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                {cost.is_recurring && (
                                  <>
                                    <DropdownMenuItem onClick={() => {
                                      setAddingEntryToCost(cost);
                                      setIsAddEntryDialogOpen(true);
                                    }}>
                                      <Plus className="w-4 h-4 mr-2" /> Add Entry
                                    </DropdownMenuItem>
                                    {cost.entries?.length > 0 && (
                                      <DropdownMenuItem onClick={() => {
                                        setViewingEntriesCost(cost);
                                        setIsViewEntriesDialogOpen(true);
                                      }}>
                                        <Calendar className="w-4 h-4 mr-2" /> View Entries
                                      </DropdownMenuItem>
                                    )}
                                  </>
                                )}
                                {!cost.is_recurring && (
                                  <DropdownMenuItem asChild>
                                    <label className="cursor-pointer flex items-center">
                                      <Upload className="w-4 h-4 mr-2" /> Upload Receipt
                                      <input
                                        type="file"
                                        className="hidden"
                                        accept=".jpg,.jpeg,.png,.webp,.pdf,.heic,.heif,.gif,.tif,.tiff"
                                        onChange={(e) => {
                                          if (e.target.files[0]) {
                                            handleReceiptUpload(cost.id, e.target.files[0]);
                                          }
                                        }}
                                      />
                                    </label>
                                  </DropdownMenuItem>
                                )}
                                {cost.receipt_file_id && (
                                  <DropdownMenuItem onClick={async () => {
                                    try {
                                      const blob = await apiService.downloadHoldingCostReceipt(projectId, cost.id);
                                      const url = URL.createObjectURL(blob.data);
                                      window.open(url, '_blank');
                                    } catch (err) {
                                      toast.error('Failed to download receipt');
                                    }
                                  }}>
                                    <FileText className="w-4 h-4 mr-2" /> View Receipt
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteCost(cost.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Add Holding Cost Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Holding Cost</DialogTitle>
          </DialogHeader>
          
          {/* AI Receipt Extraction */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-medium text-sm text-slate-800">AI Receipt Scanner</p>
                  <p className="text-xs text-slate-500">Upload a receipt to auto-fill the form</p>
                </div>
              </div>
              <label className="cursor-pointer">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={isExtractingReceipt}
                  className="bg-white"
                  asChild
                >
                  <span>
                    {isExtractingReceipt ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full mr-2" />
                        Extracting...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Receipt
                      </>
                    )}
                  </span>
                </Button>
                <input
                  type="file"
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.webp,.pdf,.heic,.heif,.gif,.tif,.tiff"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleExtractReceiptForAddCost(e.target.files[0]);
                    }
                  }}
                  disabled={isExtractingReceipt}
                />
              </label>
            </div>
            {addCostReceiptFile && (
              <div className="mt-2 flex items-center gap-2 text-xs text-green-600">
                <FileText className="w-4 h-4" />
                <span>{addCostReceiptFile.name}</span>
              </div>
            )}
          </div>
          
          <div className="space-y-4">
            <div>
              <Label>Category *</Label>
              <Select value={newCost.category} onValueChange={(v) => setNewCost({...newCost, category: v, name: HOLDING_COST_CATEGORY_LABELS[v]?.label || ''})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(HOLDING_COST_CATEGORY_LABELS).filter(([k]) => k !== 'loan_interest').map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Name *</Label>
              <Input
                value={newCost.name}
                onChange={(e) => setNewCost({...newCost, name: e.target.value})}
                placeholder="e.g., Monthly Electric Bill"
              />
            </div>
            
            <div>
              <Label>Vendor</Label>
              <Input
                value={newCost.vendor}
                onChange={(e) => setNewCost({...newCost, vendor: e.target.value})}
                placeholder="e.g., City Power & Light"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estimated Cost</Label>
                <Input
                  type="number"
                  value={newCost.estimated_cost}
                  onChange={(e) => setNewCost({...newCost, estimated_cost: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Actual Cost</Label>
                <Input
                  type="number"
                  value={newCost.actual_cost}
                  onChange={(e) => setNewCost({...newCost, actual_cost: e.target.value})}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div>
              <Label>Bank Draw Category</Label>
              <Select value={newCost.bank_category} onValueChange={(v) => setNewCost({...newCost, bank_category: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="__none__">None</SelectItem>
                  {HILLS_BANK_CATEGORIES.map(cat => (
                    <SelectItem key={cat.code} value={cat.fullName}>{cat.fullName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_recurring"
                checked={newCost.is_recurring}
                onCheckedChange={(checked) => setNewCost({...newCost, is_recurring: checked})}
              />
              <Label htmlFor="is_recurring" className="text-sm cursor-pointer">
                This is a recurring cost (e.g., monthly utility bill)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCost} className="bg-purple-600 hover:bg-purple-700">Add Cost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Holding Cost Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Holding Cost</DialogTitle>
          </DialogHeader>
          
          {/* AI Receipt Extraction for Edit */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-3 border border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-purple-600" />
                <div>
                  <p className="font-medium text-sm text-slate-800">AI Receipt Scanner</p>
                  <p className="text-xs text-slate-500">Upload to auto-fill fields</p>
                </div>
              </div>
              <label className="cursor-pointer">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={isExtractingReceipt}
                  className="bg-white h-8"
                  asChild
                >
                  <span>
                    {isExtractingReceipt ? (
                      <>
                        <div className="animate-spin w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full mr-1" />
                        Extracting...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-3 h-3 mr-1" />
                        Scan Receipt
                      </>
                    )}
                  </span>
                </Button>
                <input
                  type="file"
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.webp,.pdf,.heic,.heif,.gif,.tif,.tiff"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    setIsExtractingReceipt(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      const res = await apiService.extractReceiptData(formData);
                      
                      if (res.data.success && res.data.extracted_data) {
                        const data = res.data.extracted_data;
                        
                        // Determine category from content
                        let category = editFormData.category;
                        const text = `${data.description || ''} ${data.vendor_name || ''}`.toLowerCase();
                        if (text.includes('dumpster') || text.includes('waste')) category = 'dumpster';
                        else if (text.includes('electric') || text.includes('util') || text.includes('power') || text.includes('gas') || text.includes('water')) category = 'utilities';
                        else if (text.includes('delivery')) category = 'delivery_fees';
                        else if (text.includes('clean')) category = 'cleaning';
                        else if (text.includes('toilet') || text.includes('porta')) category = 'portable_toilet';
                        else if (text.includes('secur') || text.includes('fence')) category = 'security_fencing';
                        else if (text.includes('insur')) category = 'insurance';
                        else if (text.includes('tax')) category = 'property_taxes';
                        else if (text.includes('storage')) category = 'storage';
                        
                        setEditFormData(prev => ({
                          ...prev,
                          category: category,
                          name: data.description || data.vendor_name || prev.name,
                          vendor: data.vendor_name || prev.vendor,
                          estimated_cost: data.total_amount || prev.estimated_cost,
                          actual_cost: data.total_amount || prev.actual_cost,
                          bank_category: data.suggested_bank_category || prev.bank_category
                        }));
                        toast.success('Receipt data extracted!');
                      } else {
                        toast.error('Could not extract data');
                      }
                    } catch (err) {
                      toast.error('Extraction failed');
                    } finally {
                      setIsExtractingReceipt(false);
                    }
                  }}
                  disabled={isExtractingReceipt}
                />
              </label>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select value={editFormData.category} onValueChange={(v) => setEditFormData({...editFormData, category: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(HOLDING_COST_CATEGORY_LABELS).filter(([k]) => k !== 'loan_interest').map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Name</Label>
              <Input
                value={editFormData.name}
                onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Vendor</Label>
              <Input
                value={editFormData.vendor || ''}
                onChange={(e) => setEditFormData({...editFormData, vendor: e.target.value})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estimated Cost</Label>
                <Input
                  type="number"
                  value={editFormData.estimated_cost}
                  onChange={(e) => setEditFormData({...editFormData, estimated_cost: e.target.value})}
                />
              </div>
              <div>
                <Label>Actual Cost</Label>
                <Input
                  type="number"
                  value={editFormData.actual_cost}
                  onChange={(e) => setEditFormData({...editFormData, actual_cost: e.target.value})}
                  disabled={editFormData.is_recurring}
                />
                {editFormData.is_recurring && (
                  <p className="text-xs text-slate-500 mt-1">Calculated from entries</p>
                )}
              </div>
            </div>
            
            <div>
              <Label>Bank Draw Category</Label>
              <Select value={editFormData.bank_category || '__none__'} onValueChange={(v) => setEditFormData({...editFormData, bank_category: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="__none__">None</SelectItem>
                  {HILLS_BANK_CATEGORIES.map(cat => (
                    <SelectItem key={cat.code} value={cat.fullName}>{cat.fullName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {!editFormData.is_recurring && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit_is_paid"
                  checked={editFormData.is_paid}
                  onCheckedChange={(checked) => setEditFormData({...editFormData, is_paid: checked})}
                />
                <Label htmlFor="edit_is_paid" className="text-sm cursor-pointer">
                  Mark as Paid
                </Label>
              </div>
            )}
            
            {!editFormData.is_recurring && editFormData.is_paid && (
              <div>
                <Label>Paid Date</Label>
                <Input
                  type="date"
                  value={editFormData.paid_date || ''}
                  onChange={(e) => setEditFormData({...editFormData, paid_date: e.target.value})}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateCost} className="bg-purple-600 hover:bg-purple-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Entry Dialog (for recurring costs) */}
      <Dialog open={isAddEntryDialogOpen} onOpenChange={setIsAddEntryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Monthly Entry</DialogTitle>
            {addingEntryToCost && (
              <p className="text-sm text-slate-500">{addingEntryToCost.name}</p>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={newEntry.date}
                onChange={(e) => setNewEntry({...newEntry, date: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Amount *</Label>
              <Input
                type="number"
                value={newEntry.amount}
                onChange={(e) => setNewEntry({...newEntry, amount: e.target.value})}
                placeholder="0.00"
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Input
                value={newEntry.notes}
                onChange={(e) => setNewEntry({...newEntry, notes: e.target.value})}
                placeholder="e.g., March electric bill"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="entry_is_paid"
                checked={newEntry.is_paid}
                onCheckedChange={(checked) => setNewEntry({...newEntry, is_paid: checked})}
              />
              <Label htmlFor="entry_is_paid" className="text-sm cursor-pointer">
                Mark as Paid
              </Label>
            </div>
            
            {newEntry.is_paid && (
              <div>
                <Label>Paid Date</Label>
                <Input
                  type="date"
                  value={newEntry.paid_date}
                  onChange={(e) => setNewEntry({...newEntry, paid_date: e.target.value})}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddEntryDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddEntry} className="bg-purple-600 hover:bg-purple-700">Add Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Entries Dialog */}
      <Dialog open={isViewEntriesDialogOpen} onOpenChange={setIsViewEntriesDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Entry History</DialogTitle>
            {viewingEntriesCost && (
              <p className="text-sm text-slate-500">{viewingEntriesCost.name}</p>
            )}
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto">
            {viewingEntriesCost?.entries?.length > 0 ? (
              <div className="space-y-2">
                {viewingEntriesCost.entries.map((entry, idx) => (
                  <div key={entry.id || idx} className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium">
                          {entry.date?.split('T')[0] || entry.date}
                        </span>
                        {entry.is_paid && (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                      {entry.notes && (
                        <p className="text-xs text-slate-500 mt-1">{entry.notes}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">${(entry.amount || 0).toLocaleString()}</p>
                      {entry.is_paid && entry.paid_date && (
                        <p className="text-xs text-green-600">
                          Paid {entry.paid_date?.split('T')[0] || entry.paid_date}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">No entries yet</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewEntriesDialogOpen(false)}>Close</Button>
            <Button 
              onClick={() => {
                setIsViewEntriesDialogOpen(false);
                setAddingEntryToCost(viewingEntriesCost);
                setIsAddEntryDialogOpen(true);
              }}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HoldingCostsCard;
